/* Clear all and Adopt from… (UND-987): the two operations that replace an application's complete
   self-reported loss run in one PUT, outside the per-row staging session. No network here — this is
   the pure shaping/selection logic both the confirmation modal and the PUT payload read from. */

import { DateTime } from 'luxon';
import { capitalize } from '../../ui/utils';
import { isSkyward } from './lossStaging';
import { toDetailPayload } from './saveLossChanges';

/** The self-reported subset of a merged loss history read (drops Skyward rows). Suppressed
    self-reported rows are kept — Clear all and Adopt from… both act on an application's complete
    self-reported set, not just what the grid currently shows. */
export const selfReportedRows = (lossHistory) => (Array.isArray(lossHistory) ? lossHistory.filter((row) => !isSkyward(row)) : []);

/** Adopt from…'s PUT payload: the source application's self-reported rows (suppressed ones
    included), ids stripped so every row inserts fresh against the target application, and
    `locationId` cleared on any row whose location isn't one of the target's own locations. */
export const buildAdoptedPayload = (sourceLossHistory, targetApplication) => {
  const targetLocationIds = new Set((targetApplication.locations || []).map((location) => location.id));
  return selfReportedRows(sourceLossHistory).map((row) => {
    const clone = { ...row, id: undefined };
    if (clone.locationId && !targetLocationIds.has(clone.locationId)) {
      clone.locationId = null;
    }
    return toDetailPayload(clone, targetApplication.id);
  });
};

const latestBy = (quotes, key) => quotes.reduce((latest, quote) => (!latest || (quote[key] || 0) > (latest[key] || 0) ? quote : latest), null);

/** The quote a candidate application's picker row, and the modal/notice text describing it, are
    read from: its bound quote when it has one (an application binds at most once), else its most
    recently created quote. */
export const primaryQuoteFor = (application) => {
  const quotes = Array.isArray(application.quotes) ? application.quotes : [];
  const bound = quotes.filter((quote) => quote.status === 'bound');
  return bound.length ? latestBy(bound, 'boundAt') : latestBy(quotes, 'createdAt');
};

/** Adopt from…'s preselection: among the candidate applications' quotes, the application behind
    the one with status bound and the latest boundAt. Null when none of them has ever bound. */
export const mostRecentlyBoundApplicationId = (candidateApplications) => {
  let bestId = null;
  let bestBoundAt = null;
  (candidateApplications || []).forEach((application) => {
    (application.quotes || []).forEach((quote) => {
      if (quote.status === 'bound' && typeof quote.boundAt === 'number' && (bestBoundAt === null || quote.boundAt > bestBoundAt)) {
        bestBoundAt = quote.boundAt;
        bestId = application.id;
      }
    });
  });
  return bestId;
};

// coverageAt/coverageUntil are a Postgres DATE, sent as UTC-midnight epoch millis — read back in UTC
// so the calendar date shown here can't drift by a day the way a local-zone read could.
const formatCoverageDate = (ms) => DateTime.fromMillis(ms, { zone: 'utc' }).toFormat('MM-dd-yyyy');

export const formatTerm = (quote) =>
  quote && quote.coverageAt != null && quote.coverageUntil != null
    ? `${formatCoverageDate(quote.coverageAt)} – ${formatCoverageDate(quote.coverageUntil)}`
    : 'No term set';

/** What the picker row, the confirmation modal and the post-adopt notice all call the source
    application: `<application> · <term>`, per the spec's own placeholder. */
export const describeCandidate = (application) => {
  const quote = primaryQuoteFor(application);
  return {
    quote,
    term: formatTerm(quote),
    status: quote ? quote.status : 'unquoted',
    policyNumber: quote ? quote.policyNumber : null,
    label: `${application.insured || 'Application'} · ${formatTerm(quote)}`,
  };
};

/** A candidate's full row in Adopt from…'s picker list — `<application> — <Status> — <term>`, then
    the policy number when it has one — and the text typing into the picker is matched against, so
    an underwriter can narrow by any of them. */
export const candidateOptionText = (application) => {
  const info = describeCandidate(application);
  return [application.insured || 'Application', capitalize(info.status), info.term, info.policyNumber].filter(Boolean).join(' — ');
};

/** Adopt from…'s picker filter: every whitespace-separated word typed must appear somewhere in the
    candidate's row, in any order and case — "acme 2026" finds Acme's 2026 term without typing the
    separators between them. An empty query matches everything. */
export const candidateMatchesQuery = (application, query) => {
  const text = candidateOptionText(application).toLowerCase();
  return String(query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => text.includes(word));
};
